/**
 * Friendly Error Message Utility
 *
 * Converts raw Firebase, Firestore, network, and generic errors
 * into user-friendly messages that non-technical users can understand.
 *
 * Usage:
 *   import { getFriendlyErrorMessage } from '../utils/friendlyError';
 *   showError('Title', getFriendlyErrorMessage(error), 5000);
 */

import { getAuthErrorMessage, isFirebaseAuthError } from './authErrorHandler';

/**
 * Map of Firestore error codes to friendly messages.
 */
const FIRESTORE_ERROR_MESSAGES = {
  'permission-denied':
    'You do not have permission to perform this action. Please refresh the page and try again.',
  'not-found':
    'The requested item could not be found. It may have been deleted or moved.',
  'already-exists':
    'This item already exists. Please refresh and try again.',
  'failed-precondition':
    'This action could not be completed right now. Please try again.',
  'aborted':
    'The operation was interrupted. Please try again.',
  'out-of-range':
    'The request was invalid. Please check your input and try again.',
  'unauthenticated':
    'Your session has expired. Please sign in again to continue.',
  'resource-exhausted':
    'The service is busy right now. Please wait a moment and try again.',
  'cancelled':
    'The request was cancelled. Please try again.',
  'data-loss':
    'A data error occurred. Please try again or contact support if the problem persists.',
  'unknown':
    'Something went wrong on our end. Please try again or contact support if the problem persists.',
  'internal':
    'Something went wrong on our end. Please try again or contact support if the problem persists.',
  'invalid-argument':
    'Some of the information provided was invalid. Please check your input and try again.',
  'unavailable':
    'The service is temporarily unavailable. Please check your connection and try again in a moment.',
  'deadline-exceeded':
    'The request took too long to complete. Please check your connection and try again.',
};

/**
 * Patterns that indicate a raw technical error message that should
 * NOT be shown directly to users.
 */
const RAW_ERROR_PATTERNS = [
  /Firebase:/i,
  /Firestore:/i,
  /function\./i,
  /\[object Object\]/i,
  /undefined/i,
  /ECONNREFUSED/i,
  /ENOTFOUND/i,
  /stack:/i,
  /at \/functions\//i,
  /at Object\./i,
  /code:\s*'/i,
];

/**
 * Checks if a string looks like a raw technical error message.
 */
const isRawTechnicalMessage = (msg) => {
  if (!msg || typeof msg !== 'string') return true;
  return RAW_ERROR_PATTERNS.some((pattern) => pattern.test(msg));
};

/**
 * Converts any error into a user-friendly message.
 *
 * @param {Error|{code?: string, message?: string}|string|undefined} error
 * @param {string} fallback - Message to use if no friendly match is found
 * @returns {string} A user-friendly error message
 */
export const getFriendlyErrorMessage = (error, fallback = 'Something went wrong. Please try again.') => {
  // No error or empty
  if (!error) return fallback;

  // String passed directly
  if (typeof error === 'string') {
    return isRawTechnicalMessage(error) ? fallback : error;
  }

  // Firebase Auth errors
  if (isFirebaseAuthError(error)) {
    return getAuthErrorMessage(error);
  }

  // Firestore / Cloud Function errors (have a `code` property)
  if (error.code) {
    // Firestore codes may be prefixed with "firestore/" or "functions/"
    const normalizedCode = error.code
      .replace(/^(firestore|functions|storage)\//, '');

    if (FIRESTORE_ERROR_MESSAGES[normalizedCode]) {
      return FIRESTORE_ERROR_MESSAGES[normalizedCode];
    }

    // Storage errors
    if (error.code.startsWith('storage/')) {
      if (error.code === 'storage/object-not-found') {
        return 'The file could not be found. It may have been moved or deleted.';
      }
      if (error.code === 'storage/unauthorized') {
        return 'You do not have permission to access this file.';
      }
      if (error.code === 'storage/canceled') {
        return 'The upload was cancelled. Please try again.';
      }
      if (error.code === 'storage/quota-exceeded') {
        return 'Storage limit reached. Please contact support.';
      }
      if (error.code === 'storage/unauthenticated') {
        return 'Your session has expired. Please sign in again.';
      }
      return 'A file error occurred. Please try again.';
    }

    // Functions errors — often have a `details` or raw message
    if (error.code.startsWith('functions/')) {
      return 'The service encountered an error. Please try again or contact support if the problem persists.';
    }
  }

  // Network errors
  if (error.name === 'NetworkError' || error.message?.includes('network')) {
    return 'Network error. Please check your internet connection and try again.';
  }
  if (error.message?.includes('timeout') || error.message?.includes('deadline')) {
    return 'The request timed out. Please check your connection and try again.';
  }

  // If the error message is user-friendly (not raw technical), use it
  if (error.message && !isRawTechnicalMessage(error.message)) {
    return error.message;
  }

  // Last resort
  return fallback;
};

/**
 * Splits a single message into a title + body for showError.
 * If the message is short, returns it as the title with a generic body.
 * If it contains a sentence break, splits into title + detail.
 *
 * @param {string} message
 * @returns {{title: string, message: string}}
 */
export const splitErrorToTitleMessage = (message) => {
  if (!message) {
    return { title: 'Error', message: 'Something went wrong. Please try again.' };
  }

  // If the message is short enough to be a title on its own
  if (message.length <= 60 && !message.includes('. ')) {
    return { title: message, message: 'Please try again.' };
  }

  // Try to split at first sentence break
  const idx = message.indexOf('. ');
  if (idx > 0 && idx < 70) {
    return {
      title: message.substring(0, idx + 1),
      message: message.substring(idx + 2),
    };
  }

  // Default: use a generic title and the full message as body
  return { title: 'Error', message };
};

export default { getFriendlyErrorMessage, splitErrorToTitleMessage };
